import { PaymetData, useGetPaymetByInstallmentIdQuery } from "@/services/school/commercial/installment/payment/useGetPaymentsByInstallmentIdQuery";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  CheckIcon,
  CrossIcon,
  DeleteIcon,
  InfoIcon,
  XIcon,
} from "lucide-react";
import { toYMD } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

const usePaymentTracking = () => {
  const { id, installmentIndex } = useParams();
  const [editingPdc, setEditingPdc] = useState<PaymetData | null>(null);
  const queryClient = useQueryClient();

  const invoiceId = queryClient.getQueryData<string>(["current-invoice-id"]);

  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleAddNew = () => {
    setEditingPdc(null);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingPdc(null);
  };

  const { data: paymentData, isLoading: isPaymentDataLoading } =
    useGetPaymetByInstallmentIdQuery(installmentIndex || "", {
      enabled: !!installmentIndex,
    });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleEdit = (pdc: PaymetData) => {
    setEditingPdc(pdc);
    setIsFormOpen(true);
  };

  const columns: ColumnDef<PaymetData>[] = useMemo(
    () => [
      {
        accessorKey: "paidAmount",
        header: "Paid Amount",
        cell: (row: any) => (
          <span
            className="text-primary font-normal hover:cursor-pointer"
            onClick={() => {
              handleEdit(row?.row?.original);
            }}
          >
            {formatCurrency(row?.row?.original?.paidAmount || "")}
          </span>
        ),
      },
      {
        accessorKey: "paidDate",
        header: "Paid Date",
        cell: ({ getValue }) => (
          <span className="text-gray-800">
            {formatDate(getValue() as string)}
          </span>
        ),
      },
      {
        accessorKey: "paymentStatus",
        header: "Payment Status",
      },
      {
        accessorKey: "isPaymentReceivedByCS",
        header: "Payment Received by Cognospace",
        cell: ({ getValue }) => {
          return (
            <span className="text-gray-800">
              {getValue() ? (
                <CheckIcon className="size-8 text-green" />
              ) : (
                <XIcon className="size-8 text-red-400" />
              )}
            </span>
          );
        },
      },
      {
        accessorKey: "paymentReceivedOn",
        header: "Payment Received On",
        cell: ({ getValue }) => {
          const date = getValue() as string;
          return (
            <span className="text-gray-800">
              {date ? formatDate(date) : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "paymentRemarksHistory",
        header: "Remarks",
        cell: ({ row }) => {
          const paymentRemarksHistory = row.original.paymentRemarksHistory;
          if (!paymentRemarksHistory || paymentRemarksHistory.length === 0) {
            return <span className="text-gray-400">-</span>;
          }

          return (
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <InfoIcon className="w-4 h-4 text-blue-500 cursor-pointer" />
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="right"
                    align="center"
                    className="bg-white border rounded shadow-lg p-2 m-2 text-md text-gray-800"
                  >
                    <ul className="space-y-1">
                      {paymentRemarksHistory.map((item, idx) => (
                        <li key={idx}>
                          <span className="font-semibold text-dark-blue-600">
                            {idx + 1}. Remark By {item?.createdByData?.name} on{" "}
                            {toYMD(item?.createdAt)} -
                          </span>{" "}
                          {item.paymentRemarks}
                        </li>
                      ))}
                    </ul>
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          );
        },
      },
    ],
    []
  );

  return {
    paymentData: paymentData?.data || [],
    isLoading: isPaymentDataLoading,
    columns,
    isFormOpen,
    handleAddNew,
    handleFormClose,
    editingPdc,
  };
};
export default usePaymentTracking;
